/**
 * The add-players panel's Watchlists / My Roster tab bodies (SE-5490).
 *
 * ## Reuse
 *
 * This does NOT re-implement the watchlist/roster pools. It composes the exact
 * hooks and list organism the legacy `PlayerSidebar` uses — `useWatchlistQuery`,
 * `usePlayerQuery`, `useSidebarState`, `useSidebarInteraction` and
 * `PlayerListSection` — so pagination, filtering, drag-to-board and the
 * already-on-board markers behave identically on both rails. SE-6554 gives
 * `PlayerListSection` a `drawer` variant for the compact card and drops the
 * `WatchlistSection` picker; both surfaces still run the same queries.
 *
 * What it deliberately does not reuse is the `PlayerSidebar` ORGANISM itself.
 * That component owns a rail shell (fixed `w-80`, its own testID, the
 * drag-removal swap) and a two-way source selector, none of which a tab body
 * should carry; nesting it inside the panel would render two rail shells and
 * two source switchers. `PlayerSidebar` is therefore left byte-identical apart
 * from the removal zone extraction, so the legacy watchlist/roster surface
 * cannot regress from this ticket.
 *
 * ## Why this is a separate component rather than hooks in the panel
 *
 * `usePlayerQuery` skips per source, but there is no combination of its inputs
 * that skips BOTH the roster and watchlist reads. Mounting this subtree only
 * while its tab is selected is what keeps the Suggested tab from firing a
 * roster or watchlist query it will never render.
 * @module features/player-kanban/components/organisms/AddPlayersSourceList
 */

import React from "react";

import type { FilterState } from "../../../constants/filterDefaults";
import type { ServerGpvRange } from "../../../utils/playerFilter";
import usePlayerQuery from "../../../hooks/usePlayerQuery";
import useSidebarInteraction from "../../../hooks/useSidebarInteraction";
import useSidebarState from "../../../hooks/useSidebarState";
import useWatchlistQuery from "../../../hooks/useWatchlistQuery";
import type { RemovePlayerHandler } from "../../../types/types";

import AddPlayersSourceListView from "./AddPlayersSourceListView";

/** Props for the AddPlayersSourceList container. */
interface AddPlayersSourceListContainerProps {
  /** Which pool to render. Never `"suggested"` — that tab has its own body. */
  readonly source: "watchlist" | "roster";
  /** Cards already on the board, to mark players that are placed. */
  readonly kanbanCards: {
    player: { id: string };
    kanbanPhase: { id: string; name: string };
  }[];
  /** Active board filters, applied to the unplaced pool. */
  readonly boardFilters?: FilterState;
  /**
   * Active GPV bounds (euros) for the server-side watchlist filter (SE-6496).
   * Null while the cost filter is inactive.
   */
  readonly serverGpvRange?: ServerGpvRange | null;
  /** SE-6951: per-card one-click add, shared with the other drawer bodies. */
  readonly onAdd: (playerId: string) => void;
  /** SE-6951: player ids with an add in flight. */
  readonly addingPlayerIds: readonly string[];
  /** Drag lifecycle callbacks, forwarded to the shared list. */
  readonly onGlobalDragStart?: (dragData?: unknown, event?: unknown) => void;
  /** Drag end callback. */
  readonly onGlobalDragEnd?: () => void;
  /** Remove-from-board handler used by the drag removal flow. */
  readonly onRemovePlayerFromBoard?: RemovePlayerHandler;
  /** Auto-scroll callback during drag. */
  readonly onAutoScroll?: (dragX: number) => void;
}

/**
 * Render the Watchlists or My Roster tab body.
 * @param root0 - Component props.
 * @param root0.source - Which pool to render.
 * @param root0.kanbanCards - Cards already on the board.
 * @param root0.boardFilters - Active board filters.
 * @param root0.serverGpvRange - Active GPV bounds for the server-side watchlist filter (SE-6496).
 * @param root0.onAdd - Per-card add callback (SE-6951).
 * @param root0.addingPlayerIds - Player ids with an add in flight (SE-6951).
 * @param root0.onGlobalDragStart - Drag start callback.
 * @param root0.onGlobalDragEnd - Drag end callback.
 * @param root0.onRemovePlayerFromBoard - Remove handler.
 * @param root0.onAutoScroll - Auto-scroll callback.
 */
const AddPlayersSourceListContainer = ({
  source,
  kanbanCards,
  boardFilters,
  serverGpvRange = null,
  onAdd,
  addingPlayerIds,
  onGlobalDragStart,
  onGlobalDragEnd,
  onRemovePlayerFromBoard,
  onAutoScroll,
}: AddPlayersSourceListContainerProps) => {
  const watchlistQuery = useWatchlistQuery();

  /*
   * The drawer reads EVERY watchlist — PC5 (SE-6951) draws the Watchlists tab
   * as one FLAT list across the org's watchlists, with no source grouping and
   * no per-watchlist narrowing (the SE-6554 filter-by-watchlist menu was
   * superseded by the SE-6949 filters panel).
   *
   * The legacy rail derives its ids from `watchListSelectedVar`, a reactive var
   * the scouting surfaces also write. The drawer deliberately does not: a
   * watchlist picked on the scouting page is not a filter the user set here,
   * and inheriting it silently showed one watchlist in a tab labelled
   * "Watchlists". So the drawer resolves to `allWatchlistIds` itself.
   */
  const watchlistIds = watchlistQuery.allWatchlistIds;

  const playerQuery = usePlayerQuery({
    playerSource: source,
    watchListSelected: watchlistQuery.watchListSelected || "",
    queryWatchlistId: watchlistQuery.queryWatchlistId,
    allWatchlistIds: watchlistQuery.allWatchlistIds,
    serverGpvRange,
    selectedWatchlistIds: watchlistIds,
  });

  const sidebarState = useSidebarState({
    processedPlayers: playerQuery.processedPlayers,
    kanbanCards,
    boardFilters,
    contractValueFilteredServerSide:
      source === "watchlist" && serverGpvRange != null,
  });

  const sidebarInteraction = useSidebarInteraction({
    onGlobalDragStart,
    onGlobalDragEnd,
    onRemovePlayerFromBoard,
    onAutoScroll,
  });

  return (
    <AddPlayersSourceListView
      players={sidebarState.filteredPlayers}
      playersOnKanban={sidebarState.playersOnKanban}
      isLoadingPlayers={playerQuery.finalPlayersLoading}
      playersError={playerQuery.finalPlayersError?.message ?? null}
      metaVariant={source}
      onAdd={onAdd}
      addingPlayerIds={addingPlayerIds}
      onPlayerClick={sidebarInteraction.handlePlayerClick}
      onGlobalDragStart={sidebarInteraction.handleGlobalDragStart}
      onGlobalDragEnd={sidebarInteraction.handleGlobalDragEnd}
      onAutoScroll={sidebarInteraction.handleAutoScroll}
      onLoadMore={playerQuery.loadMore}
    />
  );
};

export default AddPlayersSourceListContainer;
